import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api/axios';
import {
  useChampionTheme, TrophyIcon, GoldButton, GlowText, Confetti,
  MedalIcon, ChampionProgress,
} from '../../components/ChampionElements';

export default function QuizPage() {
  const { webinarId } = useParams();
  const t = useChampionTheme();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const res = await api.get(`/api/member/webinars/${webinarId}/quiz`);
        if (res.data.message) { setQuiz({ notFound: true, message: res.data.message }); }
        else {
          setQuiz(res.data);
          const initial = {}; res.data.questions?.forEach(q => { initial[q.id] = []; });
          setAnswers(initial);
        }
      } catch (err) { setQuiz({ notFound: true, message: 'Kuis tidak tersedia.' }); }
      finally { setLoading(false); }
    };
    fetchQuiz();
  }, [webinarId]);

  const handleSingleSelect = (qId, oId) => setAnswers(prev => ({ ...prev, [qId]: [oId] }));
  const handleMultiSelect = (qId, oId) => setAnswers(prev => {
    const cur = prev[qId] || [];
    return { ...prev, [qId]: cur.includes(oId) ? cur.filter(id => id !== oId) : [...cur, oId] };
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = Object.entries(answers).map(([qId, oIds]) => ({ question_id: parseInt(qId), option_ids: oIds }));
      const res = await api.post(`/api/member/quizzes/${quiz.id}/submit`, { answers: payload });
      setResult(res.data.result);
    } catch (err) { alert('Gagal mengirim jawaban.'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-center py-5"><TrophyIcon size={48} /><p className="mt-3" style={{ color: t.gold }}>Mempersiapkan kuis...</p></div>;
  if (quiz?.notFound) return <div className="text-center py-5"><i className="bi bi-patch-question" style={{ fontSize: '3rem', color: t.textMuted }}></i><p className="mt-3" style={{ color: t.textMuted }}>{quiz.message}</p></div>;

  const answeredCount = Object.values(answers).filter(a => a.length > 0).length;
  const totalQuestions = quiz.questions?.length || 0;

  // ── RESULT VIEW ──
  if (result) {
    const passed = result.passed;
    const score = result.score;
    return (
      <Confetti active={passed}>
        <div style={{ textAlign: 'center', animation: 'slide-in-up 0.6s ease', padding: '20px 0' }}>
          <div className={passed ? 'animate-trophy' : ''}>
            {passed ? <TrophyIcon size={80} /> : <MedalIcon size={72} type="silver" />}
          </div>
          <div className="mt-3 mb-2">
            {passed ? (
              <GlowText as="h3" style={{ fontSize: '2rem', textTransform: 'uppercase', letterSpacing: '3px' }}>
                <i className="bi bi-stars me-2"></i>CHAMPION!<i className="bi bi-stars ms-2"></i>
              </GlowText>
            ) : (
              <h3 style={{ color: t.textMuted, fontWeight: 700 }}>Hampir Jadi Champion!</h3>
            )}
          </div>
          <div style={{ display: 'inline-block', padding: '20px 50px', borderRadius: '24px', background: passed ? `${t.gold}12` : t.cardBg, border: `2px solid ${passed ? t.gold : t.textMuted}33`, margin: '12px 0' }}>
            <div style={{ fontSize: '3.5rem', fontWeight: 900, color: passed ? t.gold : t.textMuted, lineHeight: 1 }}>
              {score}<span style={{ fontSize: '1.2rem', fontWeight: 500, opacity: 0.7 }}>/100</span>
            </div>
          </div>
          <div className="mt-3 mb-2" style={{ color: t.textMuted, fontSize: '0.9rem' }}>
            <span style={{ color: t.success, fontWeight: 600 }}>{result.correct_answers} benar</span>{' '}dari{' '}
            <span style={{ color: t.text, fontWeight: 600 }}>{result.total_questions} soal</span>{' · '}
            Nilai kelulusan: <span style={{ color: t.gold, fontWeight: 600 }}>{result.passing_score}</span>
          </div>
          <div className="mb-3">
            {passed ? (
              <span style={{ display: 'inline-block', padding: '8px 28px', borderRadius: '30px', background: t.goldGradient, color: t.textInverse, fontWeight: 800, fontSize: '0.9rem', letterSpacing: '1px', boxShadow: `0 4px 20px ${t.gold}55` }}>
                <i className="bi bi-patch-check-fill me-2"></i>LULUS
              </span>
            ) : (
              <span style={{ display: 'inline-block', padding: '8px 28px', borderRadius: '30px', background: t.elementBg, color: t.textMuted, fontWeight: 700, border: `1px solid ${t.borderLight}`, fontSize: '0.9rem' }}>
                <i className="bi bi-arrow-repeat me-2"></i>Belum Lulus
              </span>
            )}
          </div>
          <div style={{ maxWidth: '300px', margin: '0 auto 20px' }}>
            <ChampionProgress value={score} label="Skor" height={8} />
          </div>
          {!passed && <GoldButton onClick={() => { setResult(null); setAnswers({}); }} style={{ marginTop: '4px' }}><i className="bi bi-arrow-repeat me-2"></i>Coba Lagi</GoldButton>}
        </div>
      </Confetti>
    );
  }

  // ── QUESTIONS ──
  return (
    <div style={{ animation: 'slide-in-up 0.5s ease' }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <i className="bi bi-patch-question-fill" style={{ color: t.gold, fontSize: '1.5rem' }}></i>
        <h5 style={{ color: t.gold, fontWeight: 700, margin: 0 }}>Kuis Champion</h5>
      </div>
      <p className="mb-3" style={{ color: t.textMuted, fontSize: '0.85rem' }}>Jawab semua soal dan buktikan kamu layak jadi champion!</p>

      <div className="d-flex flex-wrap align-items-center gap-3 mb-4">
        <span style={{ fontSize: '0.8rem', fontWeight: 600, background: `${t.gold}15`, border: `1px solid ${t.gold}33`, color: t.gold, borderRadius: '20px', padding: '5px 14px' }}>
          <i className="bi bi-bullseye me-1"></i>Nilai Lulus: {quiz.passing_score}
        </span>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, background: t.elementBg, border: `1px solid ${t.borderLight}`, color: t.textMuted, borderRadius: '20px', padding: '5px 14px' }}>
          <i className="bi bi-list-check me-1"></i>{totalQuestions} Soal
        </span>
        {answeredCount > 0 && (
          <span style={{ fontSize: '0.8rem', fontWeight: 600, background: `${t.success}15`, border: `1px solid ${t.success}33`, color: t.success, borderRadius: '20px', padding: '5px 14px' }}>
            <i className="bi bi-check2 me-1"></i>{answeredCount}/{totalQuestions} Terjawab
          </span>
        )}
      </div>

      <div className="mb-4"><ChampionProgress value={Math.round((answeredCount / totalQuestions) * 100)} label="Progress Kuis" height={6} /></div>

      {quiz.questions?.map((q, index) => (
        <div key={q.id} className="card mb-3 animate-slide-up" style={{
          animationDelay: `${index * 0.1}s`, backgroundColor: t.cardBg,
          border: `1px solid ${t.borderLight}`, borderLeft: `4px solid ${t.gold}55`, borderRadius: '12px',
        }}>
          <div className="card-body">
            <h6 className="mb-3" style={{ color: t.text, fontWeight: 600 }}>
              <span style={{ display: 'inline-block', width: '28px', height: '28px', lineHeight: '28px', textAlign: 'center', borderRadius: '50%', background: t.goldGradient, color: t.textInverse, fontSize: '0.8rem', fontWeight: 800, marginRight: '10px' }}>{index + 1}</span>
              {q.question_text}
            </h6>
            {q.type === 'multiple' && <small style={{ color: t.gold, display: 'block', marginBottom: '8px', marginLeft: '38px' }}><i className="bi bi-info-circle me-1"></i>Pilih semua jawaban yang benar</small>}
            {q.options?.map(opt => {
              const isChecked = (answers[q.id] || []).includes(opt.id);
              return (
                <div key={opt.id} className="form-check" style={{ marginLeft: '38px', padding: '8px 12px', borderRadius: '8px', background: isChecked ? `${t.gold}10` : 'transparent', transition: 'all 0.2s ease', cursor: 'pointer' }}
                  onClick={() => q.type === 'single' ? handleSingleSelect(q.id, opt.id) : handleMultiSelect(q.id, opt.id)}>
                  <input className="form-check-input" type={q.type === 'single' ? 'radio' : 'checkbox'} name={`q_${q.id}`} id={`opt_${opt.id}`}
                    checked={isChecked} onChange={() => {}} style={{ accentColor: t.gold, cursor: 'pointer' }} />
                  <label className="form-check-label" htmlFor={`opt_${opt.id}`} style={{ color: isChecked ? t.gold : t.text, fontWeight: isChecked ? 600 : 400, cursor: 'pointer', width: '100%' }}>{opt.option_text}</label>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <button className="btn w-100" onClick={handleSubmit} disabled={submitting || answeredCount === 0} style={{
        background: answeredCount > 0 ? t.goldGradient : t.elementBg, border: 'none',
        color: answeredCount > 0 ? t.textInverse : t.textMuted, fontWeight: 800, fontSize: '1.05rem',
        borderRadius: '14px', padding: '14px', boxShadow: answeredCount > 0 ? `0 4px 25px ${t.goldShadow}` : 'none',
        transition: 'all 0.3s ease', letterSpacing: '0.5px',
      }}>
        {submitting ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Mengirim...</> : <><i className="bi bi-trophy me-2"></i>Kumpulkan & Lihat Skor!</>}
      </button>
    </div>
  );
}
